<!-- TODO: blue color -->
<script module lang="ts">
	export type InputClasses = Partial<{
		label: string;
		helper: string;
		error: string;
		success: string;
		wrapper?: string;
		labelWrapper?: string;
		tooltip?: string;
	}>;

	export interface InputProps {
		'data-date-format'?: string;
		disabled?: boolean;
		id?: string;
		step?: string;
		readonly?: boolean;
		autocapitalize?:
			'off' | 'none' | 'words' | 'characters' | 'on' | 'sentences' | null | undefined;
		rows?: number | undefined;
		type?: string;
		onFocus?: undefined | (() => void);
		size?: 'md' | 'sm';
		required?: boolean | undefined;
		optional?: boolean | undefined;
		'aria-hidden'?: boolean | 'true' | 'false' | null | undefined;
		'aria-invalid'?: HTMLInputAttributes['aria-invalid'];
		'aria-describedby'?: string | undefined;
		inputmode?: HTMLInputAttributes['inputmode'];
		value?: string | number | undefined | null;
		dark?: boolean | undefined;
		label?: string | undefined;
		classes?: InputClasses;
		tooltip?: string | undefined;
		placeholder?: string;
		labelVariant?: TxtVariant;
		tabindex?: number | null | undefined;
		error?: false | string[] | string;
		success?: string | undefined;
		name: string;
		pattern?: string;
		dataCy: string;
		inputRef?: HTMLInputElement | HTMLTextAreaElement | undefined | null;
		onblur?: ((e: Event) => void) | null | undefined;
		oninput?: (e: Event) => void;
		tooltipColor?: string;
		onkeydown?: (e: KeyboardEvent) => void;
		onkeyup?: (e: KeyboardEvent) => void;
		onchange?: (e: Event) => void;
		onpaste?: (e: Event) => void;
		onclick?: (e: Event) => void;
		children?: import('svelte').Snippet;
		helper?: import('svelte').Snippet;
		class?: string;
		maxlength?: number;
		autocomplete?: string | null | undefined;
		style?: HTMLInputAttributes['style'];
		min?: string | number;
		max?: string | number;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import dayjs from 'dayjs';
	import { onMount } from 'svelte';
	import type { HTMLInputAttributes } from 'svelte/elements';
	import { twMerge } from 'tailwind-merge';
	import { isIosDevice } from '../../utils/isIosDevice/isIosDevice';
	import { InfoTooltip } from '../InfoTooltip';
	import { InputLabel } from '../InputLabel';
	import type { TxtVariant } from '../Txt';
	import { Txt } from '../Txt';
	import { InputError } from './InputError';

	let {
		size = 'md',
		disabled = false,
		rows = undefined,
		type = 'text',
		labelVariant = 'label3',
		required = false,
		optional = false,
		value = $bindable(undefined),
		dark = false,
		label = undefined,
		classes = {},
		tooltip = undefined,
		tooltipColor = undefined,
		placeholder = '',
		error = undefined,
		success = undefined,
		name,
		dataCy,
		inputRef = $bindable(undefined),
		children,
		helper,
		onFocus = undefined,
		onblur,
		oninput,
		onkeydown,
		onkeyup,
		onchange,
		onpaste,
		onclick,
		autocomplete,
		...rest
	}: InputProps = $props();

	// `type` is a static configuration prop; `inputType` is a local state that gets toggled imperatively in the date-input flow.

	// svelte-ignore state_referenced_locally
	let inputType = $state(type == 'date' && isIosDevice() ? 'date' : 'text');
	let checkingDateInput = $state(false);

	// Inline colour for the date flow (hidden until formatted on mount) and dark
	// mode. It is dropped for a disabled text input so the disabled text class
	// applies; an inline colour would override it.
	let inputColor = $state(
		(() => {
			if (type === 'date') {
				return dark || isIosDevice() ? '#2e2e2e' : 'white';
			}

			return dark ? 'white' : 'black';
		})()
	);

	onMount(() => {
		if (inputRef && type === 'date' && !isIosDevice()) {
			if (value) {
				inputRef.value = dayjs(value)?.format('DD/MM/YYYY');
			}
			inputColor = 'black';
		}
	});

	// The date format toggle always runs, so a caller-supplied onFocus adds behaviour
	// rather than replacing it — mirroring how handleBlur treats onblur.
	const handleFocus = () => {
		if (type === 'date' && !isIosDevice()) {
			inputType = 'date';
			setTimeout(() => {
				if (inputRef && value) {
					inputRef.value = dayjs(value)?.format('YYYY-MM-DD');
				}
			}, 0);
		}

		onFocus?.();
	};

	const handleBlur = (e: Event) => {
		if (type === 'date' && !isIosDevice()) {
			checkingDateInput = true;
			inputType = 'text';
			setTimeout(() => {
				if (inputRef && value) {
					// If the provided value is not valid, we need to corrected the binded value with the auto-formatted one before updating the input value format
					const correctedValue = dayjs(value)?.format('YYYY-MM-DD');
					if ((value as string).split('-')[0] !== correctedValue.split('-')[0]) {
						value = dayjs(value)?.format('YYYY-MM-DD');
						setTimeout(() => {
							if (inputRef) {
								inputRef.value = dayjs(value)?.format('DD/MM/YYYY');
								checkingDateInput = false;
							}
						}, 0);
					} else {
						inputRef.value = dayjs(value)?.format('DD/MM/YYYY');
						checkingDateInput = false;
					}
				} else {
					checkingDateInput = false;
				}

				if (onblur) {
					onblur(e);
				}
			}, 0);
		} else {
			if (onblur) {
				onblur(e);
			}
		}
	};

	let dataCyPrefix = $derived(`${dataCy}-input`);
</script>

{#if label}
	<div class={twMerge('mb-2 flex justify-between', classes?.labelWrapper)}>
		<InputLabel
			variant={labelVariant}
			dataCy={dataCyPrefix}
			class={classes?.label}
			for={rest.id || name}
			{dark}
			{required}
			{optional}
		>
			{label}
		</InputLabel>

		{#if tooltip}
			<InfoTooltip
				dataCy={dataCyPrefix}
				placement="top-end"
				content={tooltip}
				class={classes?.tooltip}
				color={tooltipColor}
			/>
		{/if}
	</div>
{/if}

<div class={twMerge('flex', classNames({ relative: type === 'date' }), classes.wrapper)}>
	{#if type === 'text' && rows}
		<textarea
			id={name}
			data-cy={dataCyPrefix}
			{rows}
			bind:value
			bind:this={inputRef}
			{onblur}
			{oninput}
			{onkeydown}
			{onkeyup}
			{onchange}
			{onpaste}
			{onclick}
			{name}
			{disabled}
			{placeholder}
			autocomplete={autocomplete as null | undefined}
			{...rest}
			class={twMerge(
				classNames(
					'font-silka w-full appearance-none rounded border px-3 py-2 text-[0.875rem] focus:outline-none',
					{
						'py-1.5 text-[0.75rem]': size === 'sm',
						'border-gray-200 bg-white': !dark,
						'hover:border-gray-700 focus:border-gray-700': !dark && !disabled,
						'text-gray-0 border-gray-700 bg-gray-800 focus:border-[#3898ec]': dark,
						'border-red-500': !!error && !checkingDateInput,
						// Disabled entries last so twMerge keeps them over the light/dark colours.
						'cursor-not-allowed': disabled,
						'bg-gray-50': disabled && !dark,
						'text-gray-400': disabled && !isIosDevice(),
						'text-gray-900': disabled && isIosDevice()
					}
				),
				rest.class
			)}></textarea>
	{:else}
		<input
			{placeholder}
			id={name}
			bind:this={inputRef}
			data-cy={dataCyPrefix}
			{name}
			{disabled}
			style:color={disabled && type !== 'date' ? null : inputColor}
			{...type === 'date'
				? {
						type: inputType,
						max: '9999-12-31',
						min: '1900-01-01'
					}
				: { type }}
			onfocus={handleFocus}
			onblur={handleBlur}
			{onkeydown}
			{onkeyup}
			{onchange}
			{onpaste}
			{oninput}
			{onclick}
			autocomplete={autocomplete as null | undefined}
			{...rest}
			class={twMerge(
				classNames(
					'font-silka w-full appearance-none rounded border px-3 py-2 text-[0.875rem] autofill:border-0 autofill:py-[0.5625rem] focus:outline-none',
					{
						'ios-date-input min-h-[39px] bg-white': isIosDevice() && type === 'date',
						'appearance-none': type === 'date',
						'py-1.5 text-[0.75rem]': size === 'sm',
						'bg-white': !dark,
						'hover:border-gray-700 focus:border-gray-700': !dark && !disabled,
						'border-red-500': !!error && !checkingDateInput,
						'text-gray-0 border-gray-700 bg-gray-800 focus:border-gray-400': dark,
						// Disabled entries last so twMerge keeps them over the light/dark colours.
						'cursor-not-allowed': disabled,
						'bg-gray-50': disabled && !dark,
						'text-gray-400': disabled && !isIosDevice(),
						'text-gray-900': disabled && isIosDevice()
					}
				),
				rest.class
			)}
			bind:value
		/>

		<!-- {#if inputRef && isIosDevice() && !inputRef.value && type === 'date'}
			<Txt variant="body2" class="absolute left-[13px] top-2.5 text-[0.875rem] text-gray-400"
				>Select a date</Txt
			>
		{/if} -->
	{/if}

	{@render children?.()}
</div>
{#if error && !checkingDateInput}
	<InputError class={classes.error} dataCy={`${dataCyPrefix}-error`}>{error}</InputError>
{/if}
{#if success}
	<Txt
		dataCy={`${dataCyPrefix}-success`}
		variant="label4"
		class={classNames('mt-1 text-gray-500', classes?.success)}
	>
		{success}
	</Txt>
{/if}
{#if helper}
	<Txt
		dataCy={`${dataCyPrefix}-helper`}
		variant="body3"
		class={twMerge('mt-4 text-gray-500 empty:mt-0', classes.helper)}
	>
		{@render helper?.()}
	</Txt>
{/if}

<style>
	:global(.ios-date-input::-webkit-date-and-time-value) {
		text-align: left;
	}
</style>
